import React, { useState, useEffect } from 'react';

const QuestionnaireBuilder = ({ questionnaire, onQuestionnaireChange }) => {
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    if (questionnaire && questionnaire.questions) {
      setQuestions(questionnaire.questions);
    } else {
      setQuestions([]);
    }
  }, [questionnaire]);

  const generateQuestionId = () => {
    return `q${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  const addQuestion = () => {
    if (questions.length >= 5) {
      alert('Maximum of 5 questions allowed');
      return;
    }

    const newQuestion = {
      id: generateQuestionId(),
      type: 'text',
      question: '',
      required: false,
      allowMultiple: false,
      options: []
    };

    const updatedQuestions = [...questions, newQuestion];
    setQuestions(updatedQuestions);
    onQuestionnaireChange({ questions: updatedQuestions });
  };

  const removeQuestion = (questionId) => {
    const updatedQuestions = questions.filter(q => q.id !== questionId);
    setQuestions(updatedQuestions);
    onQuestionnaireChange({ questions: updatedQuestions });
  };

  const updateQuestion = (questionId, field, value) => {
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        const updated = { ...q, [field]: value };
        // Clear options when switching from multiple_choice to text
        if (field === 'type' && value === 'text') {
          updated.options = [];
        }
        // Initialize options array when switching to multiple_choice
        if (field === 'type' && value === 'multiple_choice' && !updated.options?.length) {
          updated.options = [''];
        }
        return updated;
      }
      return q;
    });
    setQuestions(updatedQuestions);
    onQuestionnaireChange({ questions: updatedQuestions });
  };

  const addOption = (questionId) => {
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        return { ...q, options: [...(q.options || []), ''] };
      }
      return q;
    });
    setQuestions(updatedQuestions);
    onQuestionnaireChange({ questions: updatedQuestions });
  };

  const updateOption = (questionId, optionIndex, value) => {
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        const newOptions = [...(q.options || [])];
        newOptions[optionIndex] = value;
        return { ...q, options: newOptions };
      }
      return q;
    });
    setQuestions(updatedQuestions);
    onQuestionnaireChange({ questions: updatedQuestions });
  };

  const removeOption = (questionId, optionIndex) => {
    const updatedQuestions = questions.map(q => {
      if (q.id === questionId) {
        const newOptions = q.options.filter((_, index) => index !== optionIndex);
        return { ...q, options: newOptions };
      }
      return q;
    });
    setQuestions(updatedQuestions);
    onQuestionnaireChange({ questions: updatedQuestions });
  };

  return (
    <div className="questionnaire-builder space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Custom Questions</h3>
          <p className="text-sm text-gray-600 mt-1">Create up to 5 custom questions for your customers</p>
        </div>
        <button
          type="button"
          className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            questions.length >= 5 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
          }`}
          onClick={addQuestion}
          disabled={questions.length >= 5}
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add Question ({questions.length}/5)
        </button>
      </div>

      {/* Empty State */}
      {questions.length === 0 && (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 text-gray-400">
            <svg fill="none" stroke="currentColor" viewBox="0 0 48 48">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" 
                    d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m-16-5c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No questions yet</h3>
          <p className="text-gray-500 mb-4">Create custom questions to gather additional information from your customers during booking.</p>
        </div>
      )}

      {/* Questions List */}
      <div className="space-y-4">
        {questions.map((question, index) => (
          <div key={question.id} className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
            {/* Question Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </div>
                <h4 className="text-lg font-medium text-gray-900">Question {index + 1}</h4>
              </div>
              <button
                type="button"
                className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                onClick={() => removeQuestion(question.id)}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remove
              </button>
            </div>

            {/* Question Configuration */}
            <div className="space-y-4">
              {/* Question Text & Type Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Text <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={question.question}
                    onChange={(e) => updateQuestion(question.id, 'question', e.target.value)}
                    placeholder="e.g., What is your experience level?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Answer Type</label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    value={question.type}
                    onChange={(e) => updateQuestion(question.id, 'type', e.target.value)}
                  >
                    <option value="text">Text Input</option>
                    <option value="multiple_choice">Multiple Choice</option>
                  </select>
                </div>
              </div>

              {/* Required Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={`required-${question.id}`}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  checked={question.required}
                  onChange={(e) => updateQuestion(question.id, 'required', e.target.checked)}
                />
                <label htmlFor={`required-${question.id}`} className="ml-2 text-sm text-gray-700">
                  Make this question required
                  {question.required && <span className="ml-1 text-red-500">*</span>}
                </label>
              </div>

              {/* Allow Multiple Selection - Only for multiple choice */}
              {question.type === 'multiple_choice' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`multiple-${question.id}`}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    checked={question.allowMultiple || false}
                    onChange={(e) => updateQuestion(question.id, 'allowMultiple', e.target.checked)}
                  />
                  <label htmlFor={`multiple-${question.id}`} className="ml-2 text-sm text-gray-700">
                    Allow multiple selections
                    <span className="ml-1 text-gray-500 text-xs">
                      ({question.allowMultiple ? 'checkboxes' : 'radio buttons'})
                    </span>
                  </label>
                </div>
              )}

              {/* Multiple Choice Options */}
              {question.type === 'multiple_choice' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">Answer Options</label>
                    <button
                      type="button"
                      className="inline-flex items-center px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                      onClick={() => addOption(question.id)}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Option
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(question.options || []).map((option, optionIndex) => (
                      <div key={optionIndex} className="flex items-center space-x-2">
                        <div className="flex-shrink-0 w-6 h-6 border border-gray-300 rounded flex items-center justify-center">
                          <div className="w-2 h-2 bg-gray-400 rounded-sm"></div>
                        </div>
                        <input
                          type="text"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          value={option}
                          onChange={(e) => updateOption(question.id, optionIndex, e.target.value)}
                          placeholder={`Option ${optionIndex + 1}`}
                        />
                        <button
                          type="button"
                          className={`p-2 text-gray-400 hover:text-red-500 transition-colors ${
                            question.options.length <= 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-50 rounded'
                          }`}
                          onClick={() => removeOption(question.id, optionIndex)}
                          disabled={question.options.length <= 1}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Info Banner */}
      {questions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <div className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-amber-900">How it works</p>
              <p className="text-sm text-amber-700 mt-1">
                These questions will appear on your public booking page. Customers must complete required questions before they can submit their booking. All responses are stored with the reservation for your review.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireBuilder;